import type {PlateTemplateCustomization} from '../../../i';
import {EP_FIT_GROUP_ATTR} from '../ep-content-utils';

export interface EPTemplateOption {
    id: number;
    name: string;
    width: number;
    height: number;
    template: string;
    lineTitles: EPTemplateLineDef[];
    // Old-style (CustomizationMethodID < 2) overlay position: inches from the frame
    // center (+X right, +Y down), same coordinate model as UV lines. showOldstyle
    // gates whether the plate is painted over the old-style product photo at all.
    // zoom scales the plate on the old-style photo only (1 = actual size, 0.95 = 95%)
    // for quirky old-style images whose proportions make the true size look wrong.
    // Optional so the hardcoded templates don't need them (they never overlay).
    xpos?: number;
    ypos?: number;
    zoom?: number;
    showOldstyle?: boolean;
    // Optional second plate (settingsJSON.plate2): its own size, HTML body (same [lineN]
    // tokens and sanitized vocabulary as `template`) and overlay position/zoom. It shares
    // plate 1's lines, colour and background; absent = single-plate template.
    plate2?: EPTemplatePlate2;
    // settingsJSON.plate_type (INST-PLATES, PRO-PLATES): the personalized-plate module's "Plate Type"
    // selector, one of two families: with_name / without_name (collegiate) or with_license /
    // without_license (profession); "any" = listed under both. Absent on every other template; a
    // product with any typed template gets the inst-plate module (inst-plate/).
    plateType?: EPPlateType;
    // settingsJSON.fit_height_margin_pt: after the width fit, the text as a whole shrinks as far as
    // needed to keep this many points of plate above and below its ink (applyEpFitToHeight).
    // Absent = no height fit.
    fitHeightMarginPt?: number;
}

/** settingsJSON.plate_type values (parseEPPlateType): the collegiate name family, the profession licence family, and "any". */
export const EP_PLATE_TYPES = ['with_name', 'without_name', 'with_license', 'without_license', 'any'] as const;
export type EPPlateType = (typeof EP_PLATE_TYPES)[number];

export type EPTemplatePlate2 = {width: number; height: number; content: string; xpos: number; ypos: number; zoom: number};

// autoFit: the line starts at autoFitPt (the admin-specified default point size,
// required by the backend editor) and shrinks to fit within 0.25" margins on either
// side of the plate as the shopper types longer text (applyEpFitToWidth bakes the
// fitted size at substitution time).
// optional: the shopper may leave the line blank (skipped by the add-to-cart check; a
// blank optional line substitutes as empty text instead of ghost text).
// plate: display-only grouping hint (1 or 2) for the inputs on the product page.
// defaultValue: ep_lines[].default, written into the line when the shopper chooses the
// template and the line is empty (resolveEPLineDefault fills its [InstName] / [GradSchool]
// / [Year] tokens); the shopper can edit it like any typed value.
// wrapChars: ep_lines[].wrap_chars, a typed value longer than this many characters is split
// into two rows of about equal length (splitEPLineForWrap), fitted to one size, instead of
// shrinking on one row (a long school name on the INST-PLATES "School & ..." templates, a long
// degree and major on their 3" x 2" and 4.75" x 2.75" "Graduate Name & Degree").
export type EPTemplateLineDef = {number: number; title: string; format?: EPTemplateLineFormat; placeholder?: string; spec?: EPTemplateLineInputSpec | null; autoFit?: boolean; autoFitPt?: number; optional?: boolean; plate?: number; defaultValue?: string; wrapChars?: number};

export type EPTemplateLineFormat = 'spaced-year' | 'spaced-digits';

export type EPTemplateLineInputSpec =
    {kind: 'dropdown'; options: string[]; placeholder: string} | {kind: 'numeric'; maxDigits: number; placeholder: string; pattern?: RegExp} | {kind: 'text'; maxLength: number; placeholder: string; pattern?: RegExp} | {kind: 'regex'; pattern: RegExp; placeholder: string};

/**
 * One entry of the { "ep_lines": [...] } JSON the backend plate-template editor
 * (FramePart_Left_Properties.ascx, EntityTypeID = 19) stores in Entities.settingsJSON.
 * `validator` is persisted alongside its compiled `regex` ("numbers" -> ^\d{N}$,
 * "text" -> ^.{0,N}$, "regex" -> the admin's own pattern, "none" -> '').
 */
type EPSettingsLine = {
    i?: number;
    title?: string;
    ph?: string;
    field_type?: string; // 'input' | 'dropdown'
    validator?: string; // 'none' | 'regex' | 'numbers' | 'text'
    regex?: string;
    length?: number;
    max_length?: number;
    options?: string[];
    auto_fit?: boolean;
    pt?: number; // auto_fit default/starting point size (required when auto_fit)
    optional?: boolean; // the shopper may leave this line blank
    plate?: number; // 1 | 2 — which plate's group the input is listed under (display only)
    default?: string; // value written into the empty line when the template is chosen ([InstName], [GradSchool], [Year] tokens)
    wrap_chars?: number; // a longer typed value splits into two rows (resolveEPTemplateContent)
};

/**
 * A line placeholder can encode which input control the line gets:
 *  - "dd::option1:option2" — dropdown of the listed options (unselected until the shopper picks; no ghost text of its own)
 *  - "n4::2022"            — integer-only input of at most 4 digits, ghost text "2022"
 *  - "t255::Facility"      — text input capped at 255 chars, ghost text "Facility"
 *  - "rgx(\d{4}):2022"     — text input validated against the regex in the parentheses,
 *                            ghost text "2022". The regex must match the whole value
 *                            (anchored like the HTML pattern attribute); a non-matching
 *                            filled-in line highlights as invalid and blocks add-to-cart.
 * A single colon is tolerated for the n/t forms ("t255:Facility"). Returns null when the
 * placeholder is none of these (or the syntax lists no usable options/limit, or the regex
 * does not compile), in which case the line stays a regular text input showing the
 * placeholder as plain ghost text.
 */
export const parseEPLineInputSpec = (placeholder?: string): EPTemplateLineInputSpec | null => {
    if (!placeholder) return null;

    // Greedy up to the last ')', so the pattern itself may contain parens/colons
    // ("rgx(^\d{2}:\d{2}$):HH:MM"). The ghost text after the closing ")<colon>" is optional.
    const rgx = placeholder.match(/^rgx\((.*)\)(?::(.*))?$/);

    if (rgx) {
        try {
            // Full-match semantics (same as <input pattern>): authors write the value's
            // shape, not their own anchors — though explicit ^/$ inside still work.
            return {kind: 'regex', pattern: new RegExp(`^(?:${rgx[1]})$`), placeholder: (rgx[2] || '').trim()};
        } catch {
            return null; // invalid regex — plain input; rawIsSyntax keeps the syntax hidden
        }
    }

    if (placeholder.startsWith('dd::')) {
        const options = placeholder
            .substring(4)
            .split(':')
            .map(o => o.trim())
            .filter(o => o !== '');

        return options.length > 0 ? {kind: 'dropdown', options, placeholder: ''} : null;
    }

    const m = placeholder.match(/^([nt])(\d+)::?(.*)$/);

    if (m) {
        // Clamp to the 255-char cap the backend applies to ep values on add-to-cart.
        const limit = Math.min(parseInt(m[2], 10), 255);
        const text = m[3].trim();

        if (limit > 0) {
            return m[1] === 'n' ? {kind: 'numeric', maxDigits: limit, placeholder: text} : {kind: 'text', maxLength: limit, placeholder: text};
        }
    }

    return null;
};

// Full-match semantics (same as <input pattern>): authors write the value's shape, not
// their own anchors — though explicit ^/$ inside still work. Null when empty or invalid.
const compileAnchoredRegex = (source?: string): RegExp | null => {
    if (!source || source.trim() === '') return null;
    try {
        return new RegExp(`^(?:${source})$`);
    } catch {
        return null;
    }
};

// Map one admin-defined settings line onto the input spec the template UI consumes; every
// spec carries the line's ghost text (ph). A dropdown with no options, a "none" validator, or
// an uncompilable regex all fall back to a plain text input (null spec).
const specFromSettingsLine = (ln: EPSettingsLine): EPTemplateLineInputSpec | null => {
    const placeholder = (ln.ph || '').trim();

    if (ln.field_type === 'dropdown') {
        const options = (ln.options || []).map(o => String(o).trim()).filter(o => o !== '');
        return options.length > 0 ? {kind: 'dropdown', options, placeholder} : null;
    }

    const pattern = compileAnchoredRegex(ln.regex) ?? undefined;

    if (ln.validator === 'numbers') {
        // Clamp to the 255-char cap the backend applies to ep values on add-to-cart.
        const maxDigits = ln.length && ln.length > 0 ? Math.min(ln.length, 255) : 255;
        return {kind: 'numeric', maxDigits, placeholder, pattern};
    }
    if (ln.validator === 'text') {
        const maxLength = ln.max_length && ln.max_length > 0 ? Math.min(ln.max_length, 255) : 255;
        return {kind: 'text', maxLength, placeholder, pattern};
    }
    if (ln.validator === 'regex' && pattern) {
        return {kind: 'regex', pattern, placeholder};
    }

    return null;
};

export type EPSettings = {
    lines: EPSettingsLine[];
    xpos: number;
    ypos: number;
    zoom: number;
    showOldstyle: boolean;
    plate2: EPTemplatePlate2 | null;
    plateType: EPPlateType | null;
    // 0 when the template has no height fit
    fitHeightMarginPt: number;
};

/**
 * settingsJSON.plate_type: "with_name" | "without_name" (the collegiate family), "with_license" |
 * "without_license" (the profession family) or "any" (case and surrounding spaces ignored).
 * Anything else, including absent, is null: an untyped template, which the inst-plate module
 * never lists and which leaves the product on the regular template UI.
 */
export const parseEPPlateType = (raw: unknown): EPPlateType | null => {
    if (typeof raw !== 'string') return null;
    const value = raw.trim().toLowerCase();
    return EP_PLATE_TYPES.find(t => t === value) ?? null;
};

/**
 * The second plate of a template (settingsJSON.plate2, authored in the backend editor's
 * "Second plate" section). Usable only when enabled with a positive size and a non-blank
 * body; anything else (absent, disabled, a draft without content) is a single-plate
 * template. Position/zoom default like plate 1's (0 / 0 / 1).
 */
export const parseEPPlate2 = (raw: unknown): EPTemplatePlate2 | null => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const p = raw as Record<string, unknown>;
    if (!p.enabled) return null;
    const width = typeof p.width === 'number' ? p.width : parseFloat(String(p.width ?? ''));
    const height = typeof p.height === 'number' ? p.height : parseFloat(String(p.height ?? ''));
    const content = typeof p.content === 'string' ? p.content : '';
    if (!(width > 0) || !(height > 0) || content.trim() === '') return null;
    return {
        width,
        height,
        content,
        xpos: typeof p.xpos === 'number' ? p.xpos : 0,
        ypos: typeof p.ypos === 'number' ? p.ypos : 0,
        zoom: typeof p.zoom === 'number' && p.zoom > 0 ? p.zoom : 1,
    };
};

// Parse a plate template's settingsJSON: { "ep_lines": [...] } plus the top-level
// old-style overlay settings (xpos / ypos / zoom / show_oldstyle), the optional
// second plate (plate2), the inst-plate type (plate_type) and the height fit's
// edge margin (fit_height_margin_pt).
export const parseEPSettings = (settingsJSON?: string): EPSettings => {
    const empty: EPSettings = {lines: [], xpos: 0, ypos: 0, zoom: 1, showOldstyle: false, plate2: null, plateType: null, fitHeightMarginPt: 0};
    if (!settingsJSON || settingsJSON.trim() === '') return empty;
    try {
        const cfg = JSON.parse(settingsJSON);
        const lines = Array.isArray(cfg) ? cfg : cfg?.ep_lines;
        return {
            lines: Array.isArray(lines) ? lines.filter(ln => ln && typeof ln === 'object') : [],
            xpos: !Array.isArray(cfg) && typeof cfg?.xpos === 'number' ? cfg.xpos : 0,
            ypos: !Array.isArray(cfg) && typeof cfg?.ypos === 'number' ? cfg.ypos : 0,
            zoom: !Array.isArray(cfg) && typeof cfg?.zoom === 'number' && cfg.zoom > 0 ? cfg.zoom : 1,
            showOldstyle: !Array.isArray(cfg) && !!cfg?.show_oldstyle,
            plate2: Array.isArray(cfg) ? null : parseEPPlate2(cfg?.plate2),
            plateType: Array.isArray(cfg) ? null : parseEPPlateType(cfg?.plate_type),
            fitHeightMarginPt: !Array.isArray(cfg) && typeof cfg?.fit_height_margin_pt === 'number' && cfg.fit_height_margin_pt > 0 ? cfg.fit_height_margin_pt : 0,
        };
    } catch {
        return empty;
    }
};

/**
 * Resolve a template line's input spec. Settings-driven lines (built from the backend's
 * per-line ep_lines settings) carry their spec directly and take precedence; lines
 * without one (hardcoded templates, DB templates without settingsJSON) fall back to the
 * legacy "dd::"/"n4::"/"t255::"/"rgx()" placeholder syntax.
 */
export const epLineSpec = (line?: {placeholder?: string; spec?: EPTemplateLineInputSpec | null}): EPTemplateLineInputSpec | null => {
    if (!line) return null;
    if (line.spec !== undefined) return line.spec;
    return parseEPLineInputSpec(line.placeholder);
};

// Recognized-but-possibly-unusable legacy control syntax ("dd::" with no options,
// "n0::", an rgx() that doesn't compile) parses to a null spec; this test keeps the raw
// syntax from leaking to the customer as ghost/placeholder text.
export const epPlaceholderIsSyntax = (placeholder?: string): boolean => !!placeholder && /^(dd::|[nt]\d+::?|rgx\()/.test(placeholder);

/**
 * Ghost text shown on the plate preview for a titled line the customer hasn't filled in
 * yet — the same text the line's input shows as its placeholder (the settings/spec ghost
 * text, or a plain non-syntax placeholder), falling back to the line's title. A dropdown
 * line ghosts like any other line (its `ph`, else its title: "State or Commonwealth" for a
 * blank State line), never its first option: a dropdown starts unselected unless the line
 * has a `default` (ep-template-apply applyTemplateLineDefaults).
 */
export const epLinePreviewGhost = (line: {title: string; placeholder?: string; spec?: EPTemplateLineInputSpec | null}): string => {
    const spec = epLineSpec(line);
    const raw = (line.placeholder || '').trim();
    const ghost = spec ? spec.placeholder : epPlaceholderIsSyntax(raw) ? '' : raw;
    return ghost || line.title;
};

// A filled-in line that fails its validation pattern (settings-driven numbers/text/regex,
// or a legacy rgx() placeholder). Blank lines are not "invalid" here — required-ness is
// the separate blank check (epTemplateMissingLineTitles / showError).
export const epLineFailsRegex = (spec: EPTemplateLineInputSpec | null, value: string): boolean => {
    if (!spec || spec.kind === 'dropdown' || value.trim() === '') return false;
    return !!spec.pattern && !spec.pattern.test(value);
};

export const formatTemplateLineValue = (value: string, format?: EPTemplateLineFormat): string => {
    if (!value || !format) return value;

    if (format === 'spaced-year') {
        const digits = value.replace(/\D/g, '');
        if (!digits) return value;
        return `· ${digits} ·`;
    }

    if (format === 'spaced-digits') {
        const digits = value.replace(/\D/g, '');
        if (!digits) return value;
        return digits.split('').join(' ');
    }

    return value;
};

const ROBOTO_STACK = "'Roboto', Poppins, 'Open Sans', 'Proxima Nova', system-ui, sans-serif";
const IBM_PLEX_MONO_STACK = "'IBM Plex Mono', Consolas, 'Courier New', monospace";
const TIMES_STACK = "'Times New Roman', Tinos, Times, serif";

const epLineDiv = (fontFamily: string, fontPt: number, linePt: number, weight: number, content: string, extra = '') =>
    `<div style="position: relative; text-align: center; font-family: ${fontFamily}; font-size: ${fontPt}pt; line-height: ${linePt}pt; font-weight: ${weight}; ${extra}">${content}</div>`;

const epFitWidthLine = (fontFamily: string, maxFontPt: number, linePt: number, weight: number, content: string, extra = '') => {
    const linePtAttr = linePt !== maxFontPt ? ` data-ep-fit-line-pt="${linePt}"` : '';
    return `<div style="position: relative; width: 87.78%; max-width: 87.78%; margin: 0 auto;"><div data-ep-fit-width data-ep-fit-max-pt="${maxFontPt}" data-ep-fit-weight="${weight}"${linePtAttr} style="position: relative; text-align: center; font-family: ${fontFamily}; font-size: ${maxFontPt}pt; line-height: ${linePt}pt; font-weight: ${weight}; ${extra}">${content}</div></div>`;
};

const triSplitLine = (label: string, epKey: string, linePt: number) =>
    `<div style="position: relative; text-align: center; font-family: ${IBM_PLEX_MONO_STACK}; font-size: 14pt; line-height: ${linePt}pt; white-space: nowrap;"><span style="font-weight: 400;">${label}</span><span style="font-weight: 400; display: inline-block; width: 2.5em; text-align: center;">....</span><span style="font-weight: 700;">${epKey}</span></div>`;

const triSplitsBlock = (lines: string, marginPt = 18) => `<div style="position: relative; margin-top: ${marginPt}pt; margin-bottom: ${marginPt}pt;">${lines}</div>`;

const ironSplitLine = (label: string, epKey: string, linePt: number) =>
    `<div style="position: relative; text-align: center; font-family: ${TIMES_STACK}; font-size: 14pt; line-height: ${linePt}pt; white-space: nowrap;"><span style="font-weight: 400;">${label}</span><span style="display: inline-block; width: 4.5em;"></span><span style="font-weight: 700;">${epKey}</span></div>`;

const FINISH_TIME_VALUE_PT = 32;
const FINISH_TIME_VALUE_LINE_PT = 34;
const FINISH_TIME_LABEL_PT = 18;
const FINISH_TIME_LABEL_LINE_PT = 18;

// Baby-Friendly Birth Facility recognition plate (13.5" x 3"). Hardcoded for testing.
// Typography matches the Calluna design spec at full plate size.
const BABY_FRIENDLY_PLATE_WIDTH = 13.5;
const BABY_FRIENDLY_PLATE_HEIGHT = 3;
const BABY_FRIENDLY_FONT_STACK = "'Calluna', 'Lora', 'PT Serif', 'Georgia', serif";
const BABY_FRIENDLY_TRACKING = 'letter-spacing: 0.025em;';

const BABY_FRIENDLY_LOGOS = {
    babyFriendlyUSA: 'https://i.diplomaframe.com/images/cached/general/baby-1.png',
    unicefUN: 'https://i.diplomaframe.com/images/cached/general/baby-2.png',
    who: 'https://i.diplomaframe.com/images/cached/general/baby-3.png',
    unicefUS: 'https://i.diplomaframe.com/images/cached/general/baby-4.png',
};

const logoImg = (src: string, alt: string, heightPt: number) => `<img src="${src}" alt="${alt}" style="position: relative; height: ${heightPt}pt; width: auto; max-width: 24%; display: block; object-fit: contain;" />`;

const lineDiv = (fontPt: number, linePt: number, weight: number, content: string, extra = '') =>
    `<div style="position: relative; text-align: center; font-family: ${BABY_FRIENDLY_FONT_STACK}; font-size: ${fontPt}pt; line-height: ${linePt}pt; font-weight: ${weight}; ${BABY_FRIENDLY_TRACKING} ${extra}">${content}</div>`;

const babyFriendlyTemplate: EPTemplateOption = {
    id: 1001,
    name: 'Baby-Friendly Birth Facility',
    width: BABY_FRIENDLY_PLATE_WIDTH,
    height: BABY_FRIENDLY_PLATE_HEIGHT,
    lineTitles: [
        {number: 1, title: 'Facility Name', placeholder: 'Facility Name'},
        {number: 2, title: 'Start Year'},
        {number: 3, title: 'End Year'},
    ],
    template: [
        lineDiv(32, 28, 700, '[[ep1]]'),
        lineDiv(13, 20, 600, 'IS HEREBY RECOGNIZED AS A'),
        lineDiv(26, 30, 700, 'BABY-FRIENDLY BIRTH FACILITY'),
        lineDiv(20, 20, 600, 'DESIGNATED (OR RE-DESIGNATED) [[ep2]] - [[ep3]]'),
        lineDiv(13, 22, 600, 'PRACTICING ALL TEN STEPS TO SUCCESSFUL BREASTFEEDING AS DEFINED BY THE WORLD HEALTH ORGANIZATION', 'white-space: nowrap;'),
        lineDiv(13, 17, 600, "AND THE UNITED NATIONS CHILDREN'S FUND"),
        `<div style="position: relative; display: flex; flex-direction: row; justify-content: space-between; align-items: flex-end; width: 95%; margin: 5px auto 0; box-sizing: border-box;">`,
        `${logoImg(BABY_FRIENDLY_LOGOS.babyFriendlyUSA, 'Baby-Friendly USA', 45)}`,
        `${logoImg(BABY_FRIENDLY_LOGOS.unicefUN, 'UNICEF UN', 45)}`,
        `${logoImg(BABY_FRIENDLY_LOGOS.who, 'World Health Organization', 45)}`,
        `${logoImg(BABY_FRIENDLY_LOGOS.unicefUS, 'UNICEF US', 45)}`,
        `</div>`,
    ].join(''),
};

const marathonFinisherTemplate: EPTemplateOption = {
    id: 1002,
    name: 'Marathon Finisher',
    width: 2.5,
    height: 3.5,
    lineTitles: [
        {number: 1, title: 'Event Name'},
        {number: 2, title: 'Year', format: 'spaced-year'},
        {number: 3, title: 'Name'},
        {number: 4, title: 'Finish Time'},
    ],
    template: [
        epFitWidthLine(ROBOTO_STACK, 12, 12, 900, '[[ep1]]', 'text-transform: uppercase;'),
        epFitWidthLine(ROBOTO_STACK, 20, 24, 700, '[[ep2]]'),
        epFitWidthLine(ROBOTO_STACK, 20, 24, 400, '[[ep3]]', 'letter-spacing: 0.2em;'),
        epFitWidthLine(IBM_PLEX_MONO_STACK, FINISH_TIME_VALUE_PT, FINISH_TIME_VALUE_LINE_PT, 400, '[[ep4]]'),
        epFitWidthLine(IBM_PLEX_MONO_STACK, FINISH_TIME_LABEL_PT, FINISH_TIME_LABEL_LINE_PT, 400, 'FINISH TIME', 'text-transform: uppercase;'),
    ].join(''),
};

const marathonTimesTemplate: EPTemplateOption = {
    id: 1003,
    name: 'Marathon Finisher (Times)',
    width: 2.5,
    height: 3.5,
    lineTitles: [
        {number: 1, title: 'Year'},
        {number: 2, title: 'City'},
        {number: 3, title: 'Marathon'},
        {number: 4, title: 'Name'},
        {number: 5, title: 'Finish Time'},
    ],
    template: [
        epFitWidthLine(ROBOTO_STACK, 36, 36, 400, '[[ep1]]', 'letter-spacing: 0.15em;'),
        epFitWidthLine(TIMES_STACK, 20, 20, 700, '[[ep2]]', 'text-transform: uppercase;'),
        epFitWidthLine(TIMES_STACK, 32, 32, 700, '[[ep3]]', 'text-transform: uppercase;'),
        epFitWidthLine(TIMES_STACK, 16, 40.5, 400, '[[ep4]]'),
        epFitWidthLine(TIMES_STACK, FINISH_TIME_VALUE_PT, FINISH_TIME_VALUE_LINE_PT, 400, '[[ep5]]'),
        epFitWidthLine(TIMES_STACK, FINISH_TIME_LABEL_PT, FINISH_TIME_LABEL_LINE_PT, 400, 'Finish Time'),
    ].join(''),
};

const triathlonFinisherTemplate: EPTemplateOption = {
    id: 1004,
    name: 'Triathlon Finisher',
    width: 2.5,
    height: 3.5,
    lineTitles: [
        {number: 1, title: 'Name'},
        {number: 2, title: 'Event'},
        {number: 3, title: 'Year', format: 'spaced-digits'},
        {number: 4, title: 'Swim Time'},
        {number: 5, title: 'Bike Time'},
        {number: 6, title: 'Run Time'},
        {number: 7, title: 'Finish Time'},
    ],
    template: [
        epLineDiv(ROBOTO_STACK, 16, 16, 400, '[[ep1]]'),
        epFitWidthLine(ROBOTO_STACK, 24, 28, 700, '[[ep2]]'),
        epFitWidthLine(ROBOTO_STACK, 20, 24, 400, '[[ep3]]', 'letter-spacing: 0.2em;'),
        triSplitsBlock([triSplitLine('SWIM', '[[ep4]]', 16.8), triSplitLine('BIKE', '[[ep5]]', 16.8), triSplitLine('RUN', '[[ep6]]', 16.8)].join('')),
        epFitWidthLine(IBM_PLEX_MONO_STACK, FINISH_TIME_VALUE_PT, FINISH_TIME_VALUE_LINE_PT, 400, '[[ep7]]'),
        epFitWidthLine(IBM_PLEX_MONO_STACK, FINISH_TIME_LABEL_PT, FINISH_TIME_LABEL_LINE_PT, 400, 'FINISH TIME', 'text-transform: uppercase;'),
    ].join(''),
};

const ironmanFinisherTemplate: EPTemplateOption = {
    id: 1005,
    name: 'Ironman Finisher',
    width: 2.5,
    height: 3.5,
    lineTitles: [
        {number: 1, title: 'Location / Date'},
        {number: 2, title: 'Name'},
        {number: 3, title: 'Swim Time'},
        {number: 4, title: 'Bike Time'},
        {number: 5, title: 'Run Time'},
        {number: 6, title: 'Finish Time'},
    ],
    template: [
        epLineDiv(TIMES_STACK, 24, 24, 700, 'IRONMAN', 'text-transform: uppercase;'),
        epFitWidthLine(TIMES_STACK, 18, 21, 400, '[[ep1]]'),
        epLineDiv(TIMES_STACK, 15, 28, 400, '[[ep2]]'),
        triSplitsBlock([ironSplitLine('Swim', '[[ep3]]', 16.8), ironSplitLine('Bike', '[[ep4]]', 16.8), ironSplitLine('Run', '[[ep5]]', 16.8)].join('')),
        epFitWidthLine(TIMES_STACK, FINISH_TIME_VALUE_PT, FINISH_TIME_VALUE_LINE_PT, 400, '[[ep6]]'),
        epFitWidthLine(TIMES_STACK, FINISH_TIME_LABEL_PT, FINISH_TIME_LABEL_LINE_PT, 400, 'Finish Time'),
    ].join(''),
};

export const HARDCODED_EP_TEMPLATES: EPTemplateOption[] = [babyFriendlyTemplate, marathonFinisherTemplate, marathonTimesTemplate, triathlonFinisherTemplate, ironmanFinisherTemplate];

/**
 * Map the product's DB-driven plate templates (PlateTemplateCustomization, built in
 * RedisCacher product.models.cs from the Frame Designer) into the EPTemplateOption shape
 * the template editor consumes.
 *
 *  - `contentText` is the plate body HTML; its [line1], [line2]… tokens mark where the
 *    user-typed text for each line is substituted (see updateTemplateContent).
 *  - `settingsJSON` ({ "ep_lines": [...] }, authored in the backend plate-template line
 *    settings editor) is the preferred source of the per-line inputs: entry N describes
 *    [lineN]'s title, placeholder and input spec (dropdown options / validator regex),
 *    replacing the legacy "dd::"/"n4::" placeholder syntax.
 *  - Templates without settings fall back to `title{N}` / `ph{N}` for line N's input
 *    label and placeholder. A line with an empty title is not an editable variable, so
 *    it is skipped (no input row).
 */
export const mapPlateTemplatesToOptions = (plateTemplates: PlateTemplateCustomization[] | undefined): EPTemplateOption[] => {
    if (!plateTemplates || plateTemplates.length === 0) return [];

    return plateTemplates.map(pt => {
        const lineTitles: EPTemplateLineDef[] = [];
        const settings = parseEPSettings(pt.settingsJSON);

        if (settings.lines.length > 0) {
            // Settings line N maps to the [lineN] token; ep{N} state caps the count at 10.
            settings.lines.slice(0, 10).forEach((ln, idx) => {
                const number = idx + 1;
                lineTitles.push({
                    number,
                    title: (ln.title || '').trim() || `Line ${number}`,
                    placeholder: (ln.ph || '').trim(),
                    spec: specFromSettingsLine(ln),
                    autoFit: !!ln.auto_fit,
                    autoFitPt: typeof ln.pt === 'number' && ln.pt > 0 ? ln.pt : undefined,
                    optional: !!ln.optional,
                    plate: Number(ln.plate) === 2 ? 2 : 1,
                    defaultValue: typeof ln.default === 'string' && ln.default.trim() !== '' ? ln.default : undefined,
                    wrapChars: typeof ln.wrap_chars === 'number' && ln.wrap_chars >= 1 ? Math.floor(ln.wrap_chars) : undefined,
                });
            });
        } else {
            for (let i = 1; i <= 8; i++) {
                const title = (pt[`title${i}`] || '').trim();
                if (!title) continue;

                lineTitles.push({
                    number: i,
                    title,
                    placeholder: (pt[`ph${i}`] || '').trim(),
                });
            }
        }

        return {
            id: pt.id,
            name: pt.title || pt.name || 'Template',
            width: pt.width > 0 ? pt.width : 3,
            height: pt.height > 0 ? pt.height : 2,
            template: pt.contentText || '',
            lineTitles,
            xpos: settings.xpos,
            ypos: settings.ypos,
            zoom: settings.zoom,
            showOldstyle: settings.showOldstyle,
            plate2: settings.plate2 || undefined,
            plateType: settings.plateType || undefined,
            fitHeightMarginPt: settings.fitHeightMarginPt || undefined,
        };
    });
};

/** What a line default's tokens resolve to on this page (INST-PLATES D5). */
export type EPLineDefaultContext = {
    // [InstName]: the store's institution display name (StoreInfo.InstName)
    instName: string;
    // [GradSchool]: the product's grad school name when the listing has one, else ''
    gradSchool: string;
    // [Year]: the current year
    year: number | string;
};

/**
 * Resolve a line's ep_lines[].default: [InstName], [GradSchool] and [Year] (any case) are
 * replaced from the context, runs of white space collapse to one space and the ends are
 * trimmed, so a blank token never leaves stray spaces. No default resolves to ''.
 */
export const resolveEPLineDefault = (defaultValue: string | undefined, ctx: EPLineDefaultContext): string => {
    if (!defaultValue) return '';
    return defaultValue
        .replace(/\[InstName\]/gi, () => ctx.instName || '')
        .replace(/\[GradSchool\]/gi, () => ctx.gradSchool || '')
        .replace(/\[Year\]/gi, () => String(ctx.year ?? ''))
        .replace(/\s+/g, ' ')
        .trim();
};

// ---------------------------------------------------------------------------
// Template line substitution (shared by both plates). Pure string functions so the
// resolved HTML can be unit-tested without a DOM; the DOM-dependent auto-fit pass
// (applyEpFitToWidth) runs on the result afterwards.
// ---------------------------------------------------------------------------

const EP_HIDE_EMPTY_ATTR = 'data-ep-hide-empty';

// True when the line's text is blank; ghost text is rendered for a required titled line
// so it is NOT "blank" here — only an untitled or optional line resolves to nothing.
const epLineResolvesBlank = (raw: string, lineDef?: EPTemplateLineDef): boolean => (raw || '').trim() === '' && (!lineDef || !!lineDef.optional);

// Line numbers of every [lineN] / [[epN]] token in a fragment.
const epTokenLineNumbers = (fragment: string): number[] => {
    const numbers: number[] = [];
    const re = /\[line(\d+)\]|\[\[ep(\d+)\]\]/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(fragment)) !== null) {
        numbers.push(parseInt(m[1] || m[2], 10));
    }
    return numbers;
};

/**
 * Remove every element marked `data-ep-hide-empty` whose [lineN] tokens all resolve to
 * blank (an optional line the shopper left empty), so an optional row collapses instead
 * of leaving a gap — the IIA "variable line spacing" case. Runs on the raw template before
 * substitution, so neither the auto-fit measurement nor the stored EPContent ever sees
 * the empty block. A marked element with no tokens, or with any non-blank token, is kept
 * (the attribute is dropped either way). Nested same-name tags are matched by depth.
 */
export const collapseEpHideEmptyBlocks = (template: string, isLineBlank: (lineNumber: number) => boolean): string => {
    if (!template || !template.includes(EP_HIDE_EMPTY_ATTR)) return template;

    const openRe = new RegExp(`<([a-zA-Z][a-zA-Z0-9]*)(\\s[^>]*?)?\\s${EP_HIDE_EMPTY_ATTR}(?:=(?:"[^"]*"|'[^']*'|[^\\s>]*))?(\\s[^>]*)?>`, 'i');
    let html = template;
    let guard = 0;

    while (guard++ < 100) {
        const open = openRe.exec(html);
        if (!open) break;

        const tag = open[1].toLowerCase();
        const start = open.index;
        const openEnd = start + open[0].length;
        const tagRe = new RegExp(`<(/?)${tag}(?=[\\s>/])[^>]*>`, 'gi');
        tagRe.lastIndex = openEnd;
        let depth = 1;
        let closeStart = -1;
        let closeEnd = -1;
        let t: RegExpExecArray | null;
        while ((t = tagRe.exec(html)) !== null) {
            depth += t[1] === '/' ? -1 : 1;
            if (depth === 0) {
                closeStart = t.index;
                closeEnd = t.index + t[0].length;
                break;
            }
        }

        // Rebuild the opening tag without the marker so a kept block is clean HTML.
        const cleanOpen = `<${open[1]}${(open[2] || '') + (open[3] || '')}>`.replace(/\s+>/, '>');

        if (closeStart < 0) {
            // Unbalanced markup: keep the block, just drop the marker so the loop ends.
            html = html.slice(0, start) + cleanOpen + html.slice(openEnd);
            continue;
        }

        const inner = html.slice(openEnd, closeStart);
        const tokens = epTokenLineNumbers(inner);
        const collapse = tokens.length > 0 && tokens.every(n => isLineBlank(n));
        html = collapse ? html.slice(0, start) + html.slice(closeEnd) : html.slice(0, start) + cleanOpen + inner + html.slice(closeStart);
    }

    return html;
};

/**
 * A wrap line's two rows (ep_lines[].wrap_chars): a value longer than `wrapChars` characters
 * splits at the space that leaves the shorter longest half (the earlier space on a tie), so
 * "School of Diplomacy and International Relations" reads "School of Diplomacy and" over
 * "International Relations". Null when the value is within the limit or has no space.
 */
export const splitEPLineForWrap = (value: string, wrapChars: number | undefined): [string, string] | null => {
    const text = (value || '').trim();
    if (!wrapChars || wrapChars < 1 || text.length <= wrapChars) return null;

    let at = -1;
    let longest = Infinity;
    for (let i = 1; i < text.length - 1; i++) {
        if (text[i] !== ' ' || text[i - 1] === ' ') continue;
        const half = Math.max(i, text.length - i - 1);
        if (half < longest) {
            at = i;
            longest = half;
        }
    }
    return at < 0 ? null : [text.slice(0, at).trim(), text.slice(at + 1).trim()];
};

// One row of a split wrap line: a block inside the template's row, which gives it the row's
// font and line-height. Inline position and width, because product.scss makes every div in
// the frame canvas absolute (and so shrink-wrapped) unless it says otherwise.
const EP_WRAP_ROW_OPEN = '<div style="position: relative; width: 100%;">';

/**
 * Resolve one plate's template HTML: collapse `data-ep-hide-empty` blocks, then swap every
 * [lineN] (DB templates) / [[epN]] (legacy hardcoded templates) token for the shopper's
 * text. A required titled line the shopper hasn't typed yet shows its placeholder dimmed
 * on the plate instead of a blank gap (add-to-cart blocks blank required lines, so a
 * ghost never reaches a real order); a blank optional line substitutes as empty text.
 * Auto Fit lines are wrapped in the marker span applyEpFitToWidth measures — it starts
 * at the admin-specified point size (autoFitPt) and shrinks within 0.25" side margins of
 * THIS plate's width. A typed value longer than its line's wrapChars becomes two block
 * rows (splitEPLineForWrap), each with its own marker span in the line's fit group (the
 * fit gives both the smaller of their sizes), so the stored EPContent carries both rows to
 * the print PDF. The replacer functions keep '$' sequences in the text literal.
 */
export const resolveEPTemplateContent = (template: string, lineValue: (lineNumber: number) => string, lineDefs: EPTemplateLineDef[] | undefined, plateWidthIn: number): string => {
    if (!template) return '';

    const defFor = (i: number) => lineDefs?.find(lt => lt.number === i);
    const autoFitWidthIn = Math.max((plateWidthIn || 2.5) - 0.5, 0.5);

    let html = collapseEpHideEmptyBlocks(template, n => epLineResolvesBlank(lineValue(n), defFor(n)));

    for (let i = 1; i <= 10; i++) {
        const raw = lineValue(i) || '';
        const lineDef = defFor(i);
        const blank = (raw || '').trim() === '';
        const value = lineDef && blank ? (lineDef.optional ? '' : `<span style="opacity: 0.4;">${epLinePreviewGhost(lineDef)}</span>`) : formatTemplateLineValue(raw, lineDef?.format);
        const fitPtStyle = lineDef?.autoFitPt && lineDef.autoFitPt > 0 ? ` style="font-size: ${lineDef.autoFitPt}pt;"` : '';
        const fit = (text: string, groupAttr = '') => (lineDef?.autoFit && text !== '' ? `<span data-ep-fit-width data-ep-fit-max-width-in="${autoFitWidthIn}"${groupAttr}${fitPtStyle}>${text}</span>` : text);
        const rows = blank ? null : splitEPLineForWrap(raw, lineDef?.wrapChars);
        const substituted = rows ? rows.map(row => `${EP_WRAP_ROW_OPEN}${fit(formatTemplateLineValue(row, lineDef?.format), ` ${EP_FIT_GROUP_ATTR}="${i}"`)}</div>`).join('') : fit(value);

        html = html.replace(new RegExp(`\\[line${i}\\]`, 'g'), () => substituted).replace(new RegExp(`\\[\\[ep${i}\\]\\]`, 'g'), () => substituted);
    }

    return html;
};
